import { useEffect, useState } from "react";
import styles from "./DossierTOC.module.css";

export type TocEntry = {
  id: string;       // anchor id on the page, e.g. "sec-hero"
  label: string;    // visible label
};

type Props = {
  entries: TocEntry[];
};

export function DossierTOC({ entries }: Props) {
  const [active, setActive] = useState<string>(entries[0]?.id ?? "");

  useEffect(() => {
    if (entries.length === 0) return;
    const ids = new Set(entries.map((e) => e.id));
    const nodes = entries
      .map((e) => document.getElementById(e.id))
      .filter((n): n is HTMLElement => Boolean(n));
    if (nodes.length === 0) return;

    const obs = new IntersectionObserver(
      (records) => {
        const visible = records
          .filter((r) => r.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const first = visible[0]?.target.id;
        if (first && ids.has(first)) setActive(first);
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: [0, 0.5, 1] }
    );

    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, [entries]);

  return (
    <aside className={styles.toc} data-rail="left">
      <div className={styles.brand}>
        <span className={styles.brandLine}>Established 2026 · Single edition</span>
        <div className={styles.wordmark}>
          Flow<em>L</em>ens<span className={styles.dot}>.</span>
        </div>
        <p className={styles.strap}>
          A forensic reading of four process-mining datasets.
        </p>
      </div>

      <div>
        <div className={styles.navLbl}>Contents</div>
        <ol className={styles.list}>
          {entries.map((e) => (
            <li key={e.id}>
              <a
                href={`#${e.id}`}
                className={`${styles.link} ${active === e.id ? styles.active : ""}`}
              >
                {e.label}
              </a>
            </li>
          ))}
        </ol>
      </div>

      <p className={styles.footnote}>
        Carlos Altamirano · Vancouver · April 2026
      </p>
    </aside>
  );
}
